/**
 * SmsOptInCheckbox, TCPA/CTIA-compliant SMS opt-in checkbox.
 *
 * Used on:
 *   • /sell  →  Instant Estimate  (InstantEstimate.jsx)
 *   • /sell  →  Manual flow       (SellValuationPage.jsx)
 *   • /repairs → Instant Estimate (InstantRepairEstimate.jsx)
 *   • /repairs → Manual ticket    (RepairFlowPage.jsx)
 *
 * Compliance notes:
 *   - Unchecked by default, opt-in must be an explicit user action.
 *   - Submission is permitted whether or not the box is checked
 *     (carriers reject "required to opt-in" patterns).
 *   - The exact disclosure text is fixed verbatim per owner directive
 *     (Feb 2026). Update only with legal review.
 */
import React from 'react';

const DISCLOSURE_TEXT =
  "I agree to receive SMS messages from 2A Electronics about my repair, appointment, and service updates. " +
  "Message frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.";

export const SMS_OPT_IN_CONSENT_TEXT = DISCLOSURE_TEXT; // for backend audit logging

export default function SmsOptInCheckbox({
  checked,
  onChange,
  accentColor = 'emerald',  // 'emerald' on sell, 'cyan' on repair
  testIdPrefix = 'sms-opt-in',
  compact = false,
}) {
  const accent = accentColor === 'cyan' ? {
    ring: 'focus:ring-cyan-500/20',
    text: 'text-cyan-500',
    link: 'text-cyan-400 hover:underline',
  } : {
    ring: 'focus:ring-emerald-500/20',
    text: 'text-emerald-500',
    link: 'text-emerald-400 hover:underline',
  };

  return (
    <div className={`flex items-start gap-2 ${compact ? 'pt-1' : 'pt-2'}`} data-testid={`${testIdPrefix}-row`}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className={`mt-1 rounded border-slate-600 ${accent.text} ${accent.ring}`}
        data-testid={`${testIdPrefix}-checkbox`}
        id={`${testIdPrefix}-checkbox`}
      />
      <label
        htmlFor={`${testIdPrefix}-checkbox`}
        className={`${compact ? 'text-[10px]' : 'text-xs'} text-slate-700 dark:text-slate-300 leading-relaxed cursor-pointer select-none`}
      >
        <strong className="text-slate-900 dark:text-white font-semibold mr-1">SMS Updates</strong>
        {DISCLOSURE_TEXT}{' '}
        See{' '}
        <a href="/sms-terms" target="_blank" rel="noreferrer" className={accent.link}>SMS Terms</a>
        {' '}and{' '}
        <a href="/privacy" target="_blank" rel="noreferrer" className={accent.link}>Privacy Policy</a>.
      </label>
    </div>
  );
}
